import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Rocket, 
  Plus, 
  Edit2, 
  Trash2, 
  Euro,
  Wallet,
  CreditCard,
  Save,
  Search,
  TrendingUp,
  Package,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  FileText,
  Eye,
  Loader2,
  ShoppingCart,
  Copy,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const ThirdPartyServicesPage = () => {
  const { theme } = useTheme();
  const [services, setServices] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('catalog');
  const [showModal, setShowModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Service form
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'hosting',
    purchase_price: '',
    sale_price: '',
    billing_type: 'monthly',
    payment_method: 'wallet',
    supplier: '',
    is_active: true
  });

  // Proposal form
  const [proposalData, setProposalData] = useState({
    client_id: '',
    services: [],
    message: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      // First try to get services, if empty, initialize defaults
      let servicesRes = await api.get('/settings/third-party-services').catch(() => ({ data: [] }));
      
      // If no services, initialize the default hosting/maintenance plans
      if (!servicesRes.data || servicesRes.data.length === 0) {
        await api.post('/settings/third-party-services/init-defaults').catch(() => {});
        servicesRes = await api.get('/settings/third-party-services').catch(() => ({ data: [] }));
      }
      
      const [proposalsRes, clientsRes] = await Promise.all([
        api.get('/settings/service-proposals').catch(() => ({ data: [] })),
        api.get('/users/', { params: { role: 'client' } }).catch(() => ({ data: [] }))
      ]);
      
      setServices(servicesRes.data || []);
      setProposals(proposalsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitDefaults = async () => {
    try {
      const res = await api.post('/settings/third-party-services/init-defaults');
      toast.success(res.data.message || 'Forfaits initialisés');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleSubmitService = async () => {
    try {
      const data = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        sale_price: parseFloat(formData.sale_price) || 0,
      };

      if (editingService) {
        await api.put(`/settings/third-party-services/${editingService.id}`, data);
        toast.success('Service modifié');
      } else {
        await api.post('/settings/third-party-services', data);
        toast.success('Service créé');
      }
      
      fetchAllData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await api.delete(`/settings/third-party-services/${id}`);
      toast.success('Service supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Duplicate a service
  const handleDuplicate = async (service) => {
    try {
      const duplicatedData = {
        name: `${service.name} (copie)`,
        description: service.description || '',
        category: service.category,
        purchase_price: service.purchase_price || 0,
        sale_price: service.sale_price || 0,
        billing_type: service.billing_type,
        payment_method: service.payment_method,
        supplier: service.supplier || '',
        is_active: true,
        sort_order: services.length
      };
      await api.post('/settings/third-party-services', duplicatedData);
      toast.success('Service dupliqué');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null) return;
    if (index !== dragOverItem) {
      setDragOverItem(index);
    }
  };

  const handleDragEnd = async () => {
    if (draggedItem === null || dragOverItem === null || draggedItem === dragOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder locally first
    const reordered = [...services];
    const [removed] = reordered.splice(draggedItem, 1);
    reordered.splice(dragOverItem, 0, removed);
    setServices(reordered);

    // Update sort_order in backend
    try {
      const updates = reordered.map((service, idx) => ({
        id: service.id,
        sort_order: idx
      }));
      await api.put('/settings/third-party-services/reorder', { services: updates });
      toast.success('Ordre mis à jour');
    } catch (error) {
      console.error('Error updating order:', error);
      // Revert on error
      fetchAllData();
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSendProposal = async () => {
    if (!proposalData.client_id || proposalData.services.length === 0) {
      toast.error('Sélectionnez un client et au moins un service');
      return;
    }

    try {
      await api.post('/settings/service-proposals', {
        ...proposalData,
        services: proposalData.services.map(id => {
          const service = services.find(s => s.id === id);
          return {
            service_id: id,
            name: service?.name,
            sale_price: service?.sale_price,
            billing_type: service?.billing_type
          };
        }),
        total_amount: proposalData.services.reduce((sum, id) => {
          const service = services.find(s => s.id === id);
          return sum + (service?.sale_price || 0);
        }, 0)
      });
      
      toast.success('Proposition envoyée au client !');
      setShowProposalModal(false);
      resetProposalForm();
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category,
      purchase_price: service.purchase_price?.toString() || '',
      sale_price: service.sale_price?.toString() || '',
      billing_type: service.billing_type,
      payment_method: service.payment_method,
      supplier: service.supplier || '',
      is_active: service.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      category: 'hosting',
      purchase_price: '',
      sale_price: '',
      billing_type: 'monthly',
      payment_method: 'wallet',
      supplier: '',
      is_active: true
    });
  };

  const resetProposalForm = () => {
    setProposalData({
      client_id: '',
      services: [],
      message: '',
      valid_until: ''
    });
  };

  const toggleServiceInProposal = (serviceId) => {
    setProposalData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      hosting: 'Hébergement',
      domain: 'Domaine',
      license: 'Licence',
      tool: 'Outil',
      other: 'Autre'
    };
    return labels[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      hosting: 'bg-blue-100 text-blue-700',
      domain: 'bg-green-100 text-green-700',
      license: 'bg-purple-100 text-purple-700',
      tool: 'bg-amber-100 text-amber-700',
      other: 'bg-slate-100 text-slate-700'
    };
    return colors[cat] || colors.other;
  };

  const getBillingLabel = (type) => {
    const labels = {
      one_time: 'Unique',
      monthly: '/mois',
      yearly: '/an'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'accepted':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Acceptée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Refusée</Badge>;
      case 'paid':
        return <Badge className="bg-blue-100 text-blue-700"><Euro className="w-3 h-3 mr-1" />Payée</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Stats
  const stats = {
    activeServices: services.filter(s => s.is_active).length,
    walletServices: services.filter(s => s.payment_method === 'wallet').length,
    separateServices: services.filter(s => s.payment_method === 'separate').length,
    avgMargin: services.length > 0 
      ? Math.round(services.reduce((sum, s) => {
          if (s.purchase_price && s.sale_price > 0) {
            return sum + ((s.sale_price - s.purchase_price) / s.sale_price * 100);
          }
          return sum;
        }, 0) / services.filter(s => s.sale_price > 0).length) || 0
      : 0,
    pendingProposals: proposals.filter(p => p.status === 'pending').length,
    acceptedProposals: proposals.filter(p => p.status === 'accepted' || p.status === 'paid').length
  };

  const filteredServices = services.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.supplier?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="third-party-services-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Services Tiers
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos services et proposez-les à vos clients
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                resetProposalForm();
                setShowProposalModal(true);
              }}
            >
              <Send className="w-4 h-4 mr-2" />
              Proposer à un client
            </Button>
            <Button 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau service
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Package className="w-4 h-4" />
                Services actifs
              </div>
              <div className="text-2xl font-bold text-slate-800">{stats.activeServices}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-indigo-600">
                <Wallet className="w-4 h-4" />
                Paiement Wallet
              </div>
              <div className="text-2xl font-bold text-indigo-700">{stats.walletServices}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-purple-600">
                <CreditCard className="w-4 h-4" />
                Paiement séparé
              </div>
              <div className="text-2xl font-bold text-purple-700">{stats.separateServices}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <TrendingUp className="w-4 h-4" />
                Marge moyenne
              </div>
              <div className="text-2xl font-bold text-emerald-700">{stats.avgMargin}%</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <Clock className="w-4 h-4" />
                En attente
              </div>
              <div className="text-2xl font-bold text-amber-700">{stats.pendingProposals}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <CheckCircle2 className="w-4 h-4" />
                Acceptées
              </div>
              <div className="text-2xl font-bold text-blue-700">{stats.acceptedProposals}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="catalog" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Catalogue
              </TabsTrigger>
              <TabsTrigger value="proposals" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Propositions
                {stats.pendingProposals > 0 && (
                  <Badge className="ml-1 bg-amber-500 text-white">{stats.pendingProposals}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            {activeTab === 'catalog' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
            )}
          </div>

          {/* Catalog Tab */}
          <TabsContent value="catalog" className="mt-4">
            {filteredServices.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Rocket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-600 mb-2">Aucun service tiers</h3>
                  <p className="text-sm text-slate-400 mb-4">Créez vos services (hébergement, domaines, licences...)</p>
                  <Button onClick={() => setShowModal(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un service
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead className="text-right">Prix achat</TableHead>
                      <TableHead className="text-right">Prix vente</TableHead>
                      <TableHead className="text-right">Marge</TableHead>
                      <TableHead>Facturation</TableHead>
                      <TableHead>Paiement</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredServices.map((service, index) => {
                      const margin = service.sale_price > 0 
                        ? Math.round((service.sale_price - service.purchase_price) / service.sale_price * 100)
                        : 0;
                      const isDragging = draggedItem === index;
                      const isDragOver = dragOverItem === index;
                      return (
                        <TableRow 
                          key={service.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            "cursor-move transition-all",
                            isDragging && "opacity-50 bg-slate-100",
                            isDragOver && "border-t-2 border-indigo-500"
                          )}
                        >
                          <TableCell className="w-10">
                            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{service.name}</div>
                              {service.supplier && (
                                <div className="text-xs text-slate-400">{service.supplier}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getCategoryColor(service.category)}>
                              {getCategoryLabel(service.category)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(service.purchase_price)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(service.sale_price)}{getBillingLabel(service.billing_type)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={margin > 50 ? "text-emerald-600 font-medium" : "text-slate-600"}>
                              {margin}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getBillingLabel(service.billing_type) === '/mois' ? 'Mensuel' :
                               getBillingLabel(service.billing_type) === '/an' ? 'Annuel' : 'Unique'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {service.payment_method === 'wallet' && (
                              <Badge className="bg-indigo-100 text-indigo-700">
                                <Wallet className="w-3 h-3 mr-1" />Wallet
                              </Badge>
                            )}
                            {service.payment_method === 'separate' && (
                              <Badge className="bg-purple-100 text-purple-700">
                                <CreditCard className="w-3 h-3 mr-1" />Séparé
                              </Badge>
                            )}
                            {service.payment_method === 'both' && (
                              <Badge className="bg-slate-100 text-slate-700">Les deux</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {service.is_active ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
                            ) : (
                              <Badge variant="outline">Inactif</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDuplicate(service)}
                                title="Dupliquer"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(service)}
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => handleDelete(service.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          {/* Proposals Tab */}
          <TabsContent value="proposals" className="mt-4">
            {proposals.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-600 mb-2">Aucune proposition</h3>
                  <p className="text-sm text-slate-400 mb-4">Proposez des services à vos clients pour qu'ils puissent les valider et payer</p>
                  <Button onClick={() => setShowProposalModal(true)} variant="outline">
                    <Send className="w-4 h-4 mr-2" />
                    Créer une proposition
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {proposals.map((proposal) => (
                  <Card key={proposal.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="font-medium">{proposal.client_name}</div>
                            <div className="text-sm text-slate-500">
                              {proposal.services?.length || 0} service(s) • Envoyée le {formatDate(proposal.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-lg font-bold">{formatCurrency(proposal.total_amount || 0)}</div>
                            {proposal.valid_until && (
                              <div className="text-xs text-slate-400">
                                Valide jusqu'au {formatDate(proposal.valid_until)}
                              </div>
                            )}
                          </div>
                          {getStatusBadge(proposal.status)}
                        </div>
                      </div>
                      {proposal.services && proposal.services.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex flex-wrap gap-2">
                            {proposal.services.map((service, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {service.name} - {formatCurrency(service.sale_price)}{getBillingLabel(service.billing_type)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Service Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingService ? 'Modifier le service' : 'Nouveau service'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom du service</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Hébergement Web Pro"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du service..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hosting">Hébergement</SelectItem>
                      <SelectItem value="domain">Domaine</SelectItem>
                      <SelectItem value="license">Licence</SelectItem>
                      <SelectItem value="tool">Outil</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fournisseur</Label>
                  <Input
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    placeholder="Ex: OVH"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix d'achat (€)</Label>
                  <Input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prix de vente (€)</Label>
                  <Input
                    type="number"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Facturation</Label>
                  <Select value={formData.billing_type} onValueChange={(v) => setFormData({ ...formData, billing_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">Paiement unique</SelectItem>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="yearly">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mode de paiement</Label>
                  <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wallet">Wallet client</SelectItem>
                      <SelectItem value="separate">Paiement séparé</SelectItem>
                      <SelectItem value="both">Les deux</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <Label>Service actif</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
              <Button onClick={handleSubmitService} style={{ backgroundColor: theme.colors?.accent }}>
                <Save className="w-4 h-4 mr-2" />
                {editingService ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Proposal Modal */}
        <Dialog open={showProposalModal} onOpenChange={setShowProposalModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Proposer des services
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select 
                    value={proposalData.client_id} 
                    onValueChange={(v) => setProposalData({ ...proposalData, client_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name || `${client.first_name} ${client.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valide jusqu'au</Label>
                  <Input
                    type="date"
                    value={proposalData.valid_until}
                    onChange={(e) => setProposalData({ ...proposalData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sélectionnez les services à proposer</Label>
                <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-1">
                  {services.filter(s => s.is_active).map((service) => {
                    const isSelected = proposalData.services.includes(service.id);
                    return (
                      <div
                        key={service.id}
                        className={cn(
                          "p-3 rounded-lg border-2 cursor-pointer transition-all",
                          isSelected 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-200 hover:border-slate-300"
                        )}
                        onClick={() => toggleServiceInProposal(service.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-sm">{service.name}</div>
                            <div className="text-xs text-slate-500">{service.supplier}</div>
                          </div>
                          {isSelected && (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <Badge className={getCategoryColor(service.category)} variant="outline">
                            {getCategoryLabel(service.category)}
                          </Badge>
                          <span className="font-bold text-sm">
                            {formatCurrency(service.sale_price)}{getBillingLabel(service.billing_type)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Message (optionnel)</Label>
                <Textarea
                  value={proposalData.message}
                  onChange={(e) => setProposalData({ ...proposalData, message: e.target.value })}
                  placeholder="Message personnalisé pour le client..."
                  rows={2}
                />
              </div>

              {/* Summary */}
              {proposalData.services.length > 0 && (
                <Card className="bg-slate-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">
                        {proposalData.services.length} service(s) sélectionné(s)
                      </span>
                      <span className="text-xl font-bold">
                        {formatCurrency(
                          proposalData.services.reduce((sum, id) => {
                            const service = services.find(s => s.id === id);
                            return sum + (service?.sale_price || 0);
                          }, 0)
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProposalModal(false)}>Annuler</Button>
              <Button 
                onClick={handleSendProposal} 
                disabled={!proposalData.client_id || proposalData.services.length === 0}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Send className="w-4 h-4 mr-2" />
                Envoyer la proposition
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default ThirdPartyServicesPage;
